import { forwardRef, useCallback } from "react";

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button";
import { Button } from "@/components/tiptap-ui-primitive/button";

// --- Icons ---
import { MathIcon } from "@/components/tiptap-icons/math-icon";

export interface MathButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
}

/**
 * Toolbar button to insert an inline math formula.
 * Uses inline editing - click the formula to edit LaTeX directly.
 */
export const MathButton = forwardRef<HTMLButtonElement, MathButtonProps>(
  ({ text, onClick, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor();

    const label = "Insert formula";

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        
        if (!editor) {
          return;
        }

        try {
          const commands = (editor as any).commands;

          if (typeof commands?.insertInlineMath !== "function") {
            console.error("MathButton: insertInlineMath command not available");
            return;
          }

          const from = editor.state.selection.from;
          // Chain insert and setNodeSelection in one transaction so selection isn't overwritten by insert
          const result = editor
            .chain()
            .focus()
            .insertInlineMath({ latex: "" })
            .setNodeSelection(from)
            .run();

          if (!result) {
            console.error("MathButton: insertInlineMath command returned false");
            return;
          }
        } catch (err) {
          console.error("MathButton: insert failed", err);
        }
      },
      [editor, onClick],
    );

    if (!editor) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        <>
          <MathIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      </Button>
    );
  },
);

MathButton.displayName = "MathButton";

